import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Calendar, Briefcase, ArrowRight } from 'lucide-react';

const HERO_IMAGE = '/images/Service_Page/Hero_1.jpeg';

const CATEGORIES = [
  {
    id: 'wedding',
    label: 'Wedding',
    icon: Heart,
    path: '/wedding',
    heroImg: '/images/Service_Page/Wedding_Hero.jpeg',
    description: 'Timeless love stories captured with cinematic elegance and heartfelt emotion.',
    highlights: [
      'Wedding Day Coverage', 'Candid & Traditional', 'Pre-Wedding Shoot',
      'Engagement Shoot', 'Haldi & Mehndi', 'Sangeet & Reception',
      'Bridal & Groom Portraits', 'Cinematic Wedding Film', 'Highlight Film',
      'Drone Coverage', 'Same-Day Edit', 'Premium Albums',
    ],
  },
  {
    id: 'fashion',
    label: 'Fashion',
    icon: Sparkles,
    path: '/fashion',
    heroImg: '/images/Service_Page/Fashion_Hero.jpeg',
    description: 'Editorial elegance meets bold creativity. Visuals that define brands and captivate audiences.',
    highlights: [
      'Fashion Show Coverage', 'Lookbook Photography', 'Model Portfolio',
      'Headshots & Comp Cards', 'Brand Campaign', 'Social Media Content',
      'Garment Photography', 'E-Commerce Catalog', 'Fashion Film',
      'Behind-the-Scenes', 'Advanced Retouching', 'Online Proofing',
    ],
  },
  {
    id: 'events',
    label: 'Events',
    icon: Calendar,
    path: '/events',
    heroImg: '/images/Service_Page/Event_Hero.jpeg',
    description: 'From corporate galas to cultural celebrations — every event deserves to be remembered beautifully.',
    highlights: [
      'Conference & Summit', 'Product Launch', 'Awards & Gala',
      'Birthday & Anniversary', 'Baby Shower', 'Private Parties',
      'Festival & Religious', 'Charity Events', 'Concert Coverage',
      'Live Streaming', 'Photo Booth', 'Event Highlight Film',
    ],
  },
  {
    id: 'commercial',
    label: 'Commercial',
    icon: Briefcase,
    path: '/commercial',
    heroImg: '/images/Service_Page/Commercial_Hero.jpeg',
    description: 'Elevate your brand with visuals that sell, inspire, and leave a lasting impression.',
    highlights: [
      'Brand Identity', 'Advertising Campaign', 'Lifestyle Photography',
      'Product Photography', 'E-Commerce Shots', '360° Product',
      'Corporate Headshots', 'Food Photography', 'Hotel & Resort',
      'Commercial Video', 'Brand Story Film', 'Rush Delivery',
    ],
  },
];

export default function ServicesPage() {
  const [activeSection, setActiveSection] = useState(CATEGORIES[0].id);
  const [subNavSticky, setSubNavSticky] = useState(false);
  const subNavRef = useRef(null);
  const subNavSentinel = useRef(null);

  /* IntersectionObserver to detect when sub-nav hits top of page */
  useEffect(() => {
    const sentinel = subNavSentinel.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setSubNavSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  /* Track which category section is in view */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-140px 0px -50% 0px' }
    );
    CATEGORIES.forEach((cat) => {
      const el = document.getElementById(cat.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToCategory = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 140;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Page Hero */}
      <section className="relative h-[45vh] sm:h-[55vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Services" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-visyra-bg via-visyra-bg/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-3">Our Expertise</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="services-page-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-visyra-text">
            Our Services
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="font-body text-base text-visyra-muted mt-3 max-w-xl">
            Comprehensive photography and cinematography services for every occasion.
          </motion.p>
        </div>
      </section>

      {/* Sentinel for detecting sub-nav stick point */}
      <div ref={subNavSentinel} className="h-0" />

      {/* Sub-Navigation Bar */}
      <div
        ref={subNavRef}
        data-testid="services-subnav"
        className={`${subNavSticky ? 'fixed top-[60px] sm:top-[68px]' : 'relative'} left-0 right-0 z-40 transition-all duration-300 ${
          subNavSticky ? 'bg-visyra-bg/95 backdrop-blur-md border-b border-white/5 shadow-lg shadow-black/20' : 'bg-visyra-bg border-b border-white/5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-0 overflow-x-auto scrollbar-hide">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeSection === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  data-testid={`subnav-${cat.id}`}
                  className={`flex items-center gap-2 px-5 sm:px-6 py-4 font-body text-sm uppercase tracking-wider whitespace-nowrap border-b-2 transition-all duration-300 ${
                    isActive
                      ? 'text-gold border-gold'
                      : 'text-visyra-muted border-transparent hover:text-gold hover:border-gold/40'
                  }`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Spacer when sub-nav is sticky */}
      {subNavSticky && <div className="h-[56px]" />}

      {/* Category Sections */}
      <div className="bg-visyra-bg">
        {CATEGORIES.map((cat, catIdx) => {
          const Icon = cat.icon;
          return (
            <section
              key={cat.id}
              id={cat.id}
              data-testid={`services-section-${cat.id}`}
              className={`py-20 sm:py-28 ${catIdx % 2 === 1 ? 'bg-visyra-paper' : 'bg-visyra-bg'}`}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                  {/* Left — Image + Info */}
                  <motion.div
                    initial={{ opacity: 0, x: catIdx % 2 === 0 ? -40 : 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className={catIdx % 2 === 1 ? 'lg:order-2' : ''}
                  >
                    <div className="relative overflow-hidden mb-8">
                      <img
                        src={cat.heroImg}
                        alt={cat.label}
                        className="w-full h-[300px] sm:h-[400px] object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex items-center gap-2">
                        <Icon size={20} className="text-gold" strokeWidth={1.5} />
                        <span className="font-heading text-lg text-white">{cat.label}</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Right — Description + Highlights Grid */}
                  <motion.div
                    initial={{ opacity: 0, x: catIdx % 2 === 0 ? 40 : -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className={catIdx % 2 === 1 ? 'lg:order-1' : ''}
                  >
                    <p className="font-body text-xs uppercase tracking-[0.3em] text-gold mb-3">{cat.label} Services</p>
                    <h2
                      data-testid={`services-heading-${cat.id}`}
                      className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-visyra-text mb-4"
                    >
                      {cat.label} <span className="italic text-gold">Photography</span>
                    </h2>
                    <div className="section-divider mb-6" />
                    <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed mb-8">
                      {cat.description}
                    </p>

                    {/* Highlights Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                      {cat.highlights.map((h) => (
                        <div
                          key={h}
                          data-testid={`highlight-${h.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center justify-center px-3 py-4 bg-visyra-card/50 border border-white/5 text-center hover:border-gold/30 transition-colors duration-300 min-h-[60px]"
                        >
                          <p className="font-body text-xs sm:text-sm text-visyra-muted leading-tight break-words">{h}</p>
                        </div>
                      ))}
                    </div>

                    {/* View Full Details Link */}
                    <Link
                      to={cat.path}
                      data-testid={`view-details-${cat.id}`}
                      className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-widest text-gold border-b border-gold pb-1 hover:text-gold-light hover:border-gold-light transition-colors duration-300"
                    >
                      View Full {cat.label} Services
                      <ArrowRight size={14} />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <section className="py-24 sm:py-32 bg-visyra-bg">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-visyra-text mb-6">
              Can't Decide? <span className="italic text-gold">Let's Talk.</span>
            </h2>
            <p className="font-body text-sm sm:text-base text-visyra-muted leading-relaxed mb-10 max-w-lg mx-auto">
              Every project is unique. Tell us your vision and we'll craft a custom package tailored to your needs.
            </p>
            <Link to="/contact" data-testid="services-cta-btn"
              className="btn-shimmer inline-block font-body text-sm font-medium uppercase tracking-widest bg-gold text-visyra-bg px-10 py-4 hover:bg-gold-light transition-all duration-400">
              Enquire Now
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
